import React, {useEffect, useState} from 'react';
import { useBrandsAndLocations } from "hooks";
import { Flex, Text, Button, Table, Avatar, Select, DropdownMenu, IconButton, AlertDialog, Badge} from "@radix-ui/themes";
import { PersonIcon, DotsHorizontalIcon} from "@radix-ui/react-icons";
import { LocationMultiSelect, TruncatedText } from "components";
import { getProfile } from "api/UsersApi";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];

// e.g. "29 Sept 2026, 11:29:23 am"
export function formatTimestamp(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const hours = d.getHours() % 12 || 12;
  const period = d.getHours() < 12 ? "am" : "pm";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hours}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${period}`;
}

export function OrgUser({ entry, orgUserId, viewerId, handleOrgUserChange, handleOrgUserDelete }) {

  const { 
    initialItems, 
    setLocationSelection, 
    setBrandSelection,
    locationsData,
    brandsData
  } = useBrandsAndLocations(viewerId);
  
  // State to hold the reconstructed brand and location selections
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  
  // Reconstruct original selections from expanded locations
  useEffect(() => {
    if (locationsData && brandsData && entry.locations && entry.locations.length > 0) {
      const userLocationIds = new Set(entry.locations);
      const reconstructedBrands = [];
      
      // Find the default brand
      const defaultBrand = brandsData.find(brand => brand.is_default);
      const defaultBrandId = defaultBrand?.id;
      
      // Check each custom brand (non-default) to see if ALL its locations are in user's location list
      brandsData.forEach(brand => {
        if (brand.id !== defaultBrandId) {
          // Find all locations that belong to this brand
          const brandLocationIds = locationsData
            .filter(location => location.brand_id === brand.id)
            .map(location => location.id);
          
          // Check if user has all locations of this brand
          const hasAllBrandLocations = brandLocationIds.length > 0 && 
                                      brandLocationIds.every(locId => userLocationIds.has(locId));
          
          if (hasAllBrandLocations) {
            // User has all locations of this brand, so they have the brand selected
            reconstructedBrands.push(brand.id);
            // Remove brand locations from the set so they don't count as independent
            brandLocationIds.forEach(locId => userLocationIds.delete(locId));
          }
        }
      });
      
      // Remaining locations are independent (should be locations with default brand)
      const independentLocationIds = Array.from(userLocationIds);
      
      setSelectedBrands(reconstructedBrands);
      setSelectedLocations(independentLocationIds);
    } else {
      setSelectedBrands([]);
      setSelectedLocations(entry.locations || []);
    }
  }, [locationsData, brandsData, entry.locations]);
  
  const handleSelectionChange = (values) => {
    const locations = [];
    const brands = [];
    
    values.forEach(value => {
      if (typeof value === 'string' && value.startsWith('brand_')) {
        // Extract brand ID from 'brand_X' format
        const brandId = parseInt(value.replace('brand_', ''));
        brands.push(brandId);
      } else {
        // It's a location ID
        locations.push(value);
      }
    });
    
    setLocationSelection(locations);
    setBrandSelection(brands);
    setSelectedLocations(locations);
    setSelectedBrands(brands);
    
    // Send both locations and brands to the backend
    handleOrgUserChange(locations, orgUserId, "locations", { brands: brands });
  }

  const handleUserRoleClick = (value) => handleOrgUserChange(value, orgUserId, "role");

  const handleCustomUserDelete = (e) => {
    handleOrgUserDelete(viewerId, orgUserId);
  }

  const [profileEmail, setProfileEmail] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await getProfile();
        setProfileEmail(profile.data.email);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
  
    fetchProfile();
  }, []);

  const isCurrentUser = !!profileEmail && entry.email === profileEmail;

  return (
    <Table.Row align="center">
      <Table.Cell>
        <Flex align="center" gap="3" style={{ minHeight: '68px', minWidth: 0 }}>
        <Avatar variant="solid" size="2" style={{ flexShrink: 0 }} fallback={(entry.first_name !== null && entry.last_name !== null) ? `${entry.first_name.slice(0, 1)}${entry.last_name.slice(0, 1)}`: <PersonIcon />} color="blue" />
        <Flex className="text-container" display="flex" direction="column" justify="center" align="start" gap="var(--space-1)" style={{flex: '1 0 0', minWidth: 0}}>
          <Flex align="center" gap="2" style={{ minWidth: 0, maxWidth: "100%" }}>
            <Text weight="regular" size="2" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {`${entry.first_name} ${entry.last_name}`}
            </Text>
            {isCurrentUser && <Badge color="green" size="1" style={{ flexShrink: 0 }}>You</Badge>}
          </Flex>
          <Text size="1" color="gray" weight="regular" 
            style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%"}}>
            {entry.email}
          </Text>
        </Flex>
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Select.Root size="2" 
          defaultValue={entry.user_role ? entry.user_role.toLowerCase() : 'manager'} 
          onValueChange={handleUserRoleClick}
          disabled={entry.email === profileEmail}
        >
          <Select.Trigger variant="ghost" color="gray" style={{width: "100%"}}/>
          <Select.Content>
            <Select.Group>
              <Select.Item value="owner">Owner</Select.Item>
              <Select.Item value="manager">Manager</Select.Item>
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </Table.Cell>
      <Table.Cell>
        {entry.user_role && entry.user_role.toLowerCase() === "owner" ? 
        <span style={{ paddingLeft: '12px', color: 'var(--gray-a11)' }}>All selected</span> :
          <LocationMultiSelect
            noShowBorder={false}
            toggleFlexGrow={true}
            options={initialItems}
            defaultValue={[
              ...selectedLocations, 
              ...selectedBrands.map(id => `brand_${id}`)
            ]}
            onValueChange={handleSelectionChange}
            displayTextType="generic"
          />
        }
      </Table.Cell>
      <Table.Cell>
        <TruncatedText>{formatTimestamp(entry.created_at) || "—"}</TruncatedText>
      </Table.Cell>
      <Table.Cell>
        <TruncatedText color={entry.last_login ? undefined : "gray"}>
          {formatTimestamp(entry.last_login) || "Never"}
        </TruncatedText>
      </Table.Cell>
      <Table.Cell justify="end">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
          {entry.email !== profileEmail ? (
            <IconButton size="1" variant='ghost' color='gray'>
              <DotsHorizontalIcon />
            </IconButton>
          ) : (
            <Text></Text>
          )}
          </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <AlertDialog.Root>
              <AlertDialog.Trigger>
                <DropdownMenu.Item color="red" onSelect={(event) => event.preventDefault()}>
                  Delete User
                </DropdownMenu.Item>
              </AlertDialog.Trigger>
              <AlertDialog.Content maxWidth="600px" maxHeight="690px">
                <AlertDialog.Title>Delete User</AlertDialog.Title>
                <AlertDialog.Description size="2">
                  This action cannot be undone.
                </AlertDialog.Description>
                <Flex gap="3" mt="4" justify="end">
                  <AlertDialog.Cancel>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action>
                    <Button variant="solid" color="red"
                      onClick={handleCustomUserDelete}>
                      Delete
                    </Button>
                  </AlertDialog.Action>
                </Flex>
              </AlertDialog.Content>
            </AlertDialog.Root>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Table.Cell>
    </Table.Row>
  );
}